import { TFunction, useTranslation } from 'react-i18next';
import {
  APIActions,
  Box,
  Button,
  CustomDialog,
  HtmlTrans,
  Icon,
  MUIDataTable,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
} from 'web-library';
import React, { useState } from 'react';



import { makeStyles } from 'tss-react/mui';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTheme } from '@mui/material';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import AddRecipientModal from './AddRecipientModal';
import { NotificationSearchRecipientResponseContentObject } from '../../../../api-refactored/manage-notifications/usePostNotifcationSearchRecipient';

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginBottom: theme.spacing(1),
    },
  },
  addRecipientButton: {
    minWidth: '200px',
  },
  sectionTitle: {
    marginBottom: theme.spacing(5),
  },
  datatable: {
    marginBottom: theme.spacing(10),
  },
}));

const tableColumns = (
  data: NotificationSearchRecipientResponseContentObject[],
  featureValue: string,
  classes: Record<string,string>,
  t: TFunction<'translation'>,
  handleOpenDeleteRecipientModal: (indexNo: string, uuid: string) => void
) => [
    {
      name: 'indexNo',
      label: t('announcementsNotifications->>SendNotification.IndexNo'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { paddingTop: '20px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography>{data[dataIndex].indexNo || '-'}</Typography>
        ),
      },
    },
    {
      name: 'nricFinPassportNo',
      label: t('announcementsNotifications->>SendNotification.NricFinPassportNo'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { paddingTop: '20px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography>{data[dataIndex].nricFinPassport}</Typography>
        ),
      },
    },
    {
      name: 'name',
      label: t('announcementsNotifications->>SendNotification.Name'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '20px' } }),
        customBodyRenderLite: (dataIndex: number) => <Typography>{data[dataIndex].name}</Typography>,
      },
    },
    {
      name: `${featureValue !== 'SMS'} ? email : sms`,
      label:
        featureValue !== 'SMS'
          ? t('announcementsNotifications->>SendNotification.EmailAddress')
          : t('announcementsNotifications->>SendNotification.MobileNo'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { paddingTop: '20px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography>{data[dataIndex].emailMobile || '-'}</Typography>
        ),
      },
    },
    {
      name: 'actions',
      label: t('announcementsNotifications->>SendNotification.Actions'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { paddingTop: '20px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.iconButtonContainer}>
            <DeleteButton
              deleteHandler={() =>
                handleOpenDeleteRecipientModal(data[dataIndex].name, data[dataIndex].uuid)
              }
              label={t('announcementsNotifications->>Listing.Delete')}
            />
          </Box>
        ),
      },
    },
  ];

interface RecipientListDataTableProps {
  featureValue: string;
  selectedCandidates: NotificationSearchRecipientResponseContentObject[];
  setSelectedCandidates: React.Dispatch<
    React.SetStateAction<NotificationSearchRecipientResponseContentObject[]>
  >;
}

const RecipientListDataTable = ({
  featureValue,
  selectedCandidates,
  setSelectedCandidates,
}: RecipientListDataTableProps) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const { classes } = useStyles();

  const [openAddRecipientModal, setOpenAddRecipientModal] = useState<boolean>(false);

  const [openDeleteRecipientModal, setOpenDeleteRecipientModal] = useState<boolean>(false);

  const [deletedRecipientDetails, setDeletedRecipientDetails] = useState<{
    name: string;
    uuid: string;
  }>({
    name: '',
    uuid: '',
  });

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: 0,
    size: 5,
    sortColumn: 'sendDate',
    order: 'desc',
  });

  const handleOpenAddRecipientModal = () => setOpenAddRecipientModal(true);

  const handleCloseAddRecipientModal = () => setOpenAddRecipientModal(false);

  const handleOpenDeleteRecipientModal = (name: string, uuid: string) => {
    setDeletedRecipientDetails({ name, uuid });
    setOpenDeleteRecipientModal(true);
  };

  const handleCloseDeleteRecipientModal = () => setOpenDeleteRecipientModal(false);

  const handleDeleteRecipientModal = () => {
    const filteredSelectedCandidates = selectedCandidates.filter(
      (selectedCandidate) => selectedCandidate.uuid !== deletedRecipientDetails.uuid
    );
    setSelectedCandidates(filteredSelectedCandidates);
    handleCloseDeleteRecipientModal();
  };

  return (
    <>
      <Box className={classes.headerContainer}>
        <Typography className={classes.sectionTitle} variant="h3">
          {t('announcementsNotifications->>SendNotification.RecipientList')}
        </Typography>
        <Button
          id="button-add-recipient"
          variant="text"
          color="primary"
          startIcon={<Icon name={IconName.add} size="L" color={theme.color.icon.iconSelected} />}
          className={classes.addRecipientButton}
          onClick={handleOpenAddRecipientModal}
        >
          <Typography variant="h4">
            {t('announcementsNotifications->>SendNotification.AddRecipient')}
          </Typography>
        </Button>
      </Box>
      <Box className={classes.datatable}>
        <MUIDataTable
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns(
            selectedCandidates,
            featureValue,
            classes,
            t,
            handleOpenDeleteRecipientModal
          )}
          data={selectedCandidates}
          emptyDataLabel={t('announcementsNotifications->>SendNotification.NoRecipientMessage')}
          options={{
            ...tOptions,
            sort: false,
            pagination: false,
          }}
        />
        <AddRecipientModal
          featureValue={featureValue}
          modalState={openAddRecipientModal}
          onDialogClose={handleCloseAddRecipientModal}
          selectedCandidates={selectedCandidates}
          setSelectedCandidates={setSelectedCandidates}
        />
        <CustomDialog
          open={openDeleteRecipientModal}
          title={t('announcementsNotifications->>SendNotification.DeleteRecipient')}
          message={
            <HtmlTrans
              i18nKey="announcementsNotifications->>SendNotification.DeleteRecipientMessage"
              values={{
                name: deletedRecipientDetails.name,
              }}
            />
          }
          action={
            <APIActions
              onClose={handleCloseDeleteRecipientModal}
              apiCall={handleDeleteRecipientModal}
              cancelString={t('announcementsNotifications->>SendNotification.Cancel')}
              apiString={t('announcementsNotifications->>SendNotification.Proceed')}
            />
          }
        />
      </Box>
    </>
  );
};

export default RecipientListDataTable;
